/**
 * Branch Pickup Page — Detailed order-style layout matching the Sales "Detailed" tab.
 *
 * Layout:
 * - Header bar: Customer, Target Branch (serving branch), Payment Method, Date
 * - ORDER LINES table: # | ITEM (inline search) | DESCRIPTION | QTY | UNIT | UNIT PRICE | SUB-TOTAL | x
 * - Right sidebar: TOTALS panel (Sub-Total, Total) + Submit button
 * - History tab
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import SmartProductSearch from '../components/SmartProductSearch';
import CalcInput from '../components/CalcInput';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GitMerge, X, ArrowRight, RefreshCw, Building2, History, Clock,
  Loader2, ChevronDown
} from 'lucide-react';

// ── Status helpers ─────────────────────────────────────────────────────────
function statusColor(status) {
  return {
    pending_confirmation: 'bg-yellow-100 text-yellow-700',
    confirmed:            'bg-blue-100 text-blue-700',
    modified:             'bg-orange-100 text-orange-700',
    otp_pending:          'bg-emerald-100 text-emerald-700',
    finalized:            'bg-green-100 text-green-700',
    cancelled:            'bg-red-100 text-red-600',
    expired:              'bg-gray-100 text-gray-500',
  }[status] || 'bg-gray-100 text-gray-500';
}
function statusLabel(s) {
  return { pending_confirmation:'Awaiting', confirmed:'Confirmed', modified:'Changes Proposed',
    otp_pending:'OTP Ready', finalized:'Finalized', cancelled:'Cancelled', expired:'Expired' }[s] || s;
}

export default function BranchPickupPage() {
  const { user, branches, effectiveBranchId, hasPerm } = useAuth();

  // ── Page tab ──
  const [activeTab, setActiveTab] = useState('create');
  const location = useLocation();
  // Honor ?tab=history (e.g. from the "View all pickups" pill link) even when
  // already on this page.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t === 'history' || t === 'create') setActiveTab(t);
  }, [location.search]);

  // ── Target branch (serving branch) ──
  const otherBranches = (branches || []).filter(b => b.id !== effectiveBranchId);
  const [servingBranchId, setServingBranchId] = useState('');
  const servingBranchName = otherBranches.find(b => b.id === servingBranchId)?.name || '';

  // ── Order lines (detailed mode) ──
  const emptyLine = () => ({ product_id: '', product_name: '', sku: '', unit: '', description: '', qty: 1, rate: 0, available: 0, is_repack: false, parent_id: null, units_per_parent: 1 });
  const [lines, setLines] = useState([emptyLine()]);
  const orderLinesEndRef = useRef(null);

  // ── Customer & payment ──
  const [customerName, setCustomerName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [submitting, setSubmitting] = useState(false);

  // ── History ──
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all');

  // ── Invoice detail modal (opened from a finalized pickup's receipt #) ──
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const openInvoiceDetail = (order) => {
    if (!order.invoice_id) {
      toast.info('No receipt yet — this pickup is not finalized.');
      return;
    }
    setSelectedInvoiceId(order.invoice_id);
    setInvoiceModalOpen(true);
  };

  const canCreate = hasPerm('branch_pickups', 'create');
  const canView   = hasPerm('branch_pickups', 'view');

  // ── Fetch history ──
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const [outRes, inRes] = await Promise.all([
        api.get('/branch-pickups/outgoing', { params: { limit: 30 } }),
        api.get('/branch-pickups/incoming', { params: { limit: 30 } }),
      ]);
      const outgoing = (outRes.data.orders || []).map(o => ({ ...o, _direction: 'outgoing' }));
      const incoming = (inRes.data.orders || []).map(o => ({ ...o, _direction: 'incoming' }));
      setHistoryOrders([...outgoing, ...incoming].sort((a, b) => b.created_at.localeCompare(a.created_at)));
    } catch { /* silent */ }
    setHistoryLoading(false);
  }, []);

  useEffect(() => { if (activeTab === 'history') fetchHistory(); }, [activeTab, fetchHistory]);

  // ── Product select (inline in order line) ──
  const handleProductSelect = useCallback((lineIndex, product) => {
    if (!servingBranchId) { toast.error('Select a target branch first'); return; }

    // Check if product already exists in lines
    const existingIdx = lines.findIndex(l => l.product_id === product.id);
    if (existingIdx >= 0 && existingIdx !== lineIndex) {
      setLines(prev => prev.map((l, i) =>
        i === existingIdx ? { ...l, qty: parseFloat(l.qty) + 1 } : l
      ));
      return;
    }

    const prices = product.prices || {};
    const defaultRate = parseFloat(prices.retail ?? prices.wholesale ?? prices.default ?? product.cost_price ?? 0);

    setLines(prev => {
      const updated = prev.map((l, i) => {
        if (i !== lineIndex) return l;
        return {
          ...l,
          product_id: product.id,
          product_name: product.name,
          sku: product.sku || '',
          unit: product.unit || '',
          description: '',
          available: parseFloat(product.stock_at_serving ?? product.available ?? 0),
          qty: 1,
          rate: defaultRate,
          is_repack: product.is_repack || false,
          parent_id: product.parent_id || null,
          units_per_parent: parseFloat(product.units_per_parent || 1),
        };
      });
      // Auto-add blank line if last line is now filled
      if (updated[updated.length - 1].product_id) {
        updated.push(emptyLine());
      }
      return updated;
    });

    // Scroll to bottom
    setTimeout(() => orderLinesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }), 100);
  }, [servingBranchId, lines]);

  const updateLine = (index, field, value) => {
    setLines(prev => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
  };

  const clearLine = (index) => {
    setLines(prev => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0 || updated[updated.length - 1].product_id) {
        updated.push(emptyLine());
      }
      return updated;
    });
  };

  const filledLines = lines.filter(l => l.product_id);
  const subtotal = filledLines.reduce((s, l) => s + parseFloat(l.qty || 0) * parseFloat(l.rate || 0), 0);

  // ── Submit ──
  const handleSubmit = async () => {
    if (!servingBranchId) { toast.error('Select a target branch'); return; }
    if (filledLines.length === 0) { toast.error('Add at least one item'); return; }

    setSubmitting(true);
    try {
      await api.post('/branch-pickups/', {
        sale_branch_id: effectiveBranchId,
        serving_branch_id: servingBranchId,
        customer_name: customerName || 'Walk-in',
        payment_type: paymentMethod.toLowerCase(),
        payment_method: paymentMethod,
        items: filledLines.map(l => ({
          product_id: l.product_id,
          qty_requested: parseFloat(l.qty),
          unit_price_requested: parseFloat(l.rate),
        })),
      });
      toast.success(`Pickup order sent to ${servingBranchName}`);
      setLines([emptyLine()]);
      setServingBranchId('');
      setCustomerName('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create pickup order');
    }
    setSubmitting(false);
  };

  // Reset lines when branch changes
  useEffect(() => { setLines([emptyLine()]); }, [servingBranchId]); // eslint-disable-line

  if (!canView) {
    return <div className="p-8 text-center text-gray-400">You do not have permission to view Branch Pickups.</div>;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] animate-fadeIn bg-white" data-testid="branch-pickup-page">

      {/* ══════════ TAB HEADER ══════════ */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-200 bg-white shrink-0">
        <div className="flex items-center gap-2">
          <GitMerge size={18} className="text-[#1A4D2E]" />
          <span className="text-base font-bold tracking-tight">Branch Pickup</span>
        </div>
        <div className="flex items-center gap-1 ml-4">
          <button
            onClick={() => setActiveTab('create')}
            data-testid="tab-create"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'create' ? 'bg-[#1A4D2E] text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            New Order
          </button>
          <button
            onClick={() => setActiveTab('history')}
            data-testid="tab-history"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'history' ? 'bg-[#1A4D2E] text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            <History size={13} /> History
          </button>
        </div>
      </div>

      {/* ══════════ HISTORY TAB ══════════ */}
      {activeTab === 'history' && (
        <div className="flex-1 overflow-auto">
          <div className="px-4 pt-3 pb-2 flex items-center justify-between border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-700">Pickup Order History</span>
            <div className="flex items-center gap-2">
              <div className="relative">
                <select
                  value={historyFilter}
                  onChange={e => setHistoryFilter(e.target.value)}
                  className="h-8 pl-2 pr-7 text-xs border border-slate-200 rounded-md bg-white appearance-none"
                  data-testid="history-filter"
                >
                  <option value="all">All</option>
                  <option value="outgoing">Outgoing</option>
                  <option value="incoming">Incoming</option>
                  <option value="finalized">Finalized</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
              <Button variant="outline" size="sm" className="h-8 gap-1" onClick={fetchHistory}>
                <RefreshCw size={13} className={historyLoading ? 'animate-spin' : ''} /> Refresh
              </Button>
            </div>
          </div>
          {historyLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <div className="px-4">
              <table className="w-full text-sm" data-testid="history-table">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Pickup #</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Direction</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Other Branch</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Total</th>
                    <th className="text-center px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Invoice</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {historyOrders
                    .filter(o => historyFilter === 'all' ? true : historyFilter === 'outgoing' ? o._direction === 'outgoing' : historyFilter === 'incoming' ? o._direction === 'incoming' : o.status === historyFilter)
                    .map(order => (
                    <tr key={order.id + order._direction} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`history-row-${order.id}`}>
                      <td className="px-3 py-2.5 font-medium">
                        {order.invoice_id ? (
                          <button
                            onClick={() => openInvoiceDetail(order)}
                            className="text-[#1A4D2E] hover:underline font-semibold"
                            title="View receipt"
                            data-testid={`history-receipt-link-${order.id}`}
                          >
                            {order.pickup_doc_number || order.pickup_number}
                          </button>
                        ) : (
                          <span className="text-slate-800">{order.pickup_number}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge className={`text-[10px] ${order._direction === 'outgoing' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
                          {order._direction === 'outgoing' ? 'Sent' : 'Received'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">{order._direction === 'outgoing' ? order.serving_branch_name : order.sale_branch_name}</td>
                      <td className="px-3 py-2.5 text-right font-semibold">{formatPHP(order.grand_total)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <Badge className={`text-[10px] ${statusColor(order.status)}`}>{statusLabel(order.status)}</Badge>
                      </td>
                      <td className="px-3 py-2.5 text-right text-xs">
                        {order.invoice_id ? (
                          <button
                            onClick={() => openInvoiceDetail(order)}
                            className="text-[#1A4D2E] hover:underline"
                            data-testid={`history-invoice-link-${order.id}`}
                          >
                            {order.invoice_number || 'View'}
                          </button>
                        ) : (
                          <span className="text-slate-400">{order.invoice_number || '—'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right text-xs text-slate-400">{order.created_at ? new Date(order.created_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                  {historyOrders.filter(o => historyFilter === 'all' ? true : historyFilter === 'outgoing' ? o._direction === 'outgoing' : historyFilter === 'incoming' ? o._direction === 'incoming' : o.status === historyFilter).length === 0 && (
                    <tr><td colSpan={7} className="py-12 text-center text-slate-400 text-sm">
                      <Clock size={28} className="mx-auto mb-2 opacity-30" />
                      No pickup orders found.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════ CREATE TAB ══════════ */}
      {activeTab === 'create' && (
        <>
          {/* ── Header bar: Customer, Target Branch, Payment, Date ── */}
          <div className="border-b border-slate-200 px-5 py-3 shrink-0 bg-white">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Customer */}
              <div className="flex items-center gap-1.5">
                <Label className="text-[10px] text-slate-400 uppercase shrink-0">Customer</Label>
                <Input
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  placeholder="Name or phone... (blank = Walk-in)"
                  className="h-9 w-56 text-sm"
                  data-testid="customer-name-input"
                />
              </div>

              {/* Target Branch (serving branch) */}
              <div className="flex items-center gap-1.5">
                <Label className="text-[10px] text-slate-400 uppercase shrink-0">Target Branch</Label>
                <div className="relative">
                  <Building2 size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <select
                    value={servingBranchId}
                    onChange={e => setServingBranchId(e.target.value)}
                    className="h-9 pl-8 pr-7 text-sm border border-slate-200 rounded-md bg-white appearance-none font-medium min-w-[180px]"
                    data-testid="serving-branch-select"
                  >
                    <option value="">Select branch...</option>
                    {otherBranches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>

              <Separator orientation="vertical" className="h-7 hidden sm:block" />

              {/* Payment Method */}
              <div className="flex items-center gap-1.5">
                <Label className="text-[10px] text-slate-400 uppercase shrink-0">Payment</Label>
                <div className="relative">
                  <select
                    value={paymentMethod}
                    onChange={e => setPaymentMethod(e.target.value)}
                    className="h-9 pl-3 pr-7 text-sm border border-slate-200 rounded-md bg-white appearance-none"
                    data-testid="payment-method-select"
                  >
                    {['Cash', 'Credit', 'GCash', 'Bank Transfer', 'Check'].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          {/* ── Main content: Order Lines + Totals sidebar ── */}
          <div className="flex flex-1 overflow-hidden">

            {/* LEFT — Order Lines */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
              {!servingBranchId ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <Building2 size={48} className="mx-auto text-slate-200 mb-3" />
                    <p className="text-slate-400 text-sm">Select a target branch to start adding products</p>
                  </div>
                </div>
              ) : (
                <Card className="flex-1 flex flex-col border-slate-200 m-3 min-h-0">
                  <CardContent className="flex-1 flex flex-col p-0 min-h-0">
                    {/* Toolbar */}
                    <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 bg-slate-50/50">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Order Lines</span>
                      {servingBranchName && (
                        <span className="text-[10px] text-teal-600 font-medium">
                          Stock & prices from {servingBranchName}
                        </span>
                      )}
                    </div>

                    {/* Table */}
                    <div className="overflow-auto flex-1">
                      <table className="w-full text-sm" data-testid="order-lines-table">
                        <thead className="sticky top-0 bg-slate-100 z-10">
                          <tr className="border-b border-slate-200">
                            <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-10">#</th>
                            <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold min-w-[240px]">Item</th>
                            <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold min-w-[120px]">Description</th>
                            <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-20">Qty</th>
                            <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-14">Unit</th>
                            <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-24">Unit Price</th>
                            <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-28">Sub-Total</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {lines.map((line, i) => (
                            <tr key={i} className={`border-b border-slate-100 transition-colors hover:bg-emerald-50/30 ${i % 2 === 1 ? 'bg-slate-50/40' : 'bg-white'}`}
                              data-testid={`order-line-${i}`}>
                              {/* # */}
                              <td className="px-2 py-0.5 text-[11px] text-slate-400 align-middle">{i + 1}</td>

                              {/* Item (inline search or product display) */}
                              <td className="px-2 py-0.5 min-w-[240px] align-middle">
                                {line.product_id ? (
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[13px] font-medium truncate">{line.product_name}</p>
                                      <p className="text-[10px] text-slate-400 leading-tight truncate">
                                        {line.sku && <span>{line.sku} · </span>}
                                        Stock: <span className={`font-medium ${line.available >= parseFloat(line.qty || 0) ? 'text-emerald-600' : 'text-red-600'}`}>
                                          {Number(line.available).toFixed(0)} {line.unit}
                                        </span>
                                      </p>
                                    </div>
                                    <button onClick={() => clearLine(i)} className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0" title="Remove">
                                      <X size={12} />
                                    </button>
                                  </div>
                                ) : (
                                  <SmartProductSearch
                                    branchId={effectiveBranchId}
                                    onSelect={(p) => handleProductSelect(i, p)}
                                    searchEndpoint="/branch-pickups/serving-branch-products"
                                    extraParams={{ serving_branch_id: servingBranchId }}
                                    placeholder="Type product name or scan barcode..."
                                  />
                                )}
                              </td>

                              {/* Description */}
                              <td className="px-2 py-0.5 align-middle">
                                <input
                                  className="w-full h-7 px-2 text-[13px] border border-transparent hover:border-slate-200 focus:border-[#1A4D2E] focus:outline-none rounded bg-transparent"
                                  value={line.description || ''}
                                  onChange={e => updateLine(i, 'description', e.target.value)}
                                  placeholder="—"
                                  disabled={!line.product_id}
                                />
                              </td>

                              {/* Qty */}
                              <td className="px-2 py-0.5 align-middle">
                                <CalcInput
                                  value={line.product_id ? line.qty : ''}
                                  onChange={v => updateLine(i, 'qty', v)}
                                  selectOnFocus
                                  className="w-full h-7 text-right text-sm px-2 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#1A4D2E]/30"
                                  disabled={!line.product_id}
                                  data-testid={`qty-input-${i}`}
                                />
                              </td>

                              {/* Unit */}
                              <td className="px-2 py-0.5 text-[12px] text-slate-500 align-middle">{line.unit || ''}</td>

                              {/* Unit Price */}
                              <td className="px-2 py-0.5 align-middle">
                                <CalcInput
                                  value={line.product_id ? line.rate : ''}
                                  onChange={v => updateLine(i, 'rate', v)}
                                  selectOnFocus
                                  className="w-full h-7 text-right text-sm px-2 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#1A4D2E]/30"
                                  disabled={!line.product_id}
                                  data-testid={`rate-input-${i}`}
                                />
                              </td>

                              {/* Sub-Total */}
                              <td className="px-2 py-0.5 text-right text-sm font-medium align-middle">
                                {line.product_id ? formatPHP(parseFloat(line.qty || 0) * parseFloat(line.rate || 0)) : ''}
                              </td>

                              {/* Remove */}
                              <td className="px-1 py-0.5 align-middle">
                                {line.product_id && (
                                  <button onClick={() => clearLine(i)} className="p-1 text-slate-300 hover:text-red-500 transition-colors" data-testid={`btn-remove-${i}`}>
                                    <X size={12} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div ref={orderLinesEndRef} aria-hidden="true" />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* RIGHT — Totals sidebar */}
            {servingBranchId && (
              <div className="w-64 shrink-0 border-l border-slate-200 bg-white flex flex-col p-4" data-testid="totals-sidebar">
                <div className="space-y-3">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Totals</p>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Sub-Total</span>
                    <span className="font-semibold" data-testid="subtotal">{formatPHP(subtotal)}</span>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">Total</span>
                    <span className="text-xl font-bold text-slate-900" data-testid="total">{formatPHP(subtotal)}</span>
                  </div>
                </div>

                <div className="mt-auto pt-4 space-y-2">
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || filledLines.length === 0 || !canCreate}
                    className="w-full bg-[#1A4D2E] hover:bg-[#14532d] text-white h-10 gap-1.5"
                    data-testid="btn-submit-pickup"
                  >
                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                    {submitting ? 'Sending...' : 'Send Pickup Request'}
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { setLines([emptyLine()]); }}
                    disabled={filledLines.length === 0}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <InvoiceDetailModal compact
        open={invoiceModalOpen}
        onOpenChange={setInvoiceModalOpen}
        saleId={selectedInvoiceId}
        onUpdated={fetchHistory}
      />
    </div>
  );
}
